import React, { useMemo, useState } from "react";
import { SelectButton } from "primereact/selectbutton";

/**
 * Esquemas de operación conocidos. Cada "momento" es un mismo viaje de la unidad:
 * a esa hora llega el personal de un turno (entrada) y se recoge al de otro (salida).
 */
const ESQUEMAS = {
    DOCE: {
        label: "12x12",
        momentos: [
            { clave: "MANANA", label: "Mañana", icon: "pi pi-sun",  color: "azul",    entrada: "E1", salida: "S2" },
            { clave: "TARDE",  label: "Tarde",  icon: "pi pi-moon", color: "naranja", entrada: "E2", salida: "S1" },
        ],
    },
    DOS: {
        label: "2 turnos",
        momentos: [
            { clave: "MANANA",   label: "Mañana",    icon: "pi pi-sun",  color: "azul",    entrada: "E1", salida: null },
            { clave: "MEDIODIA", label: "Medio día", icon: "pi pi-sync", color: "naranja", entrada: "E2", salida: "S1" },
            { clave: "NOCHE",    label: "Noche",     icon: "pi pi-moon", color: "morado",  entrada: null, salida: "S2" },
        ],
    },
    TRES: {
        label: "3 turnos",
        momentos: [
            { clave: "MANANA",   label: "Mañana",    icon: "pi pi-sun",  color: "azul",    entrada: "E1", salida: "S3" },
            { clave: "MEDIODIA", label: "Medio día", icon: "pi pi-sync", color: "naranja", entrada: "E2", salida: "S1" },
            { clave: "NOCHE",    label: "Noche",     icon: "pi pi-moon", color: "morado",  entrada: "E3", salida: "S2" },
        ],
    },
    ADMIN: {
        label: "Administrativo",
        momentos: [
            { clave: "MANANA", label: "Mañana", icon: "pi pi-sun",  color: "azul",    entrada: "EA", salida: null },
            { clave: "TARDE",  label: "Tarde",  icon: "pi pi-moon", color: "naranja", entrada: null, salida: "SA" },
        ],
    },
};

// Tolerancia para considerar que una entrada y una salida son el mismo viaje
const TOLERANCIA_MISMO_VIAJE_MIN = 90;
// Una jornada de 10 h o más se considera 12x12
const JORNADA_LARGA_MIN = 10 * 60;

// Salidas sin viaje fijo: se acomodan por horario o en la salida que le falte a un turno
const SALIDAS_FLEXIBLES = ["SSD"];

export const esEntrada = (turno) => turno?.startsWith("E");

/** Entrada del mismo turno del personal (S1 → E1, SA → EA). */
const entradaDeSalida = (salida) => salida.replace(/^S/, "E");

/** Hora relevante de la estructura: entrada = llegada (horaFin), salida = partida (horaInicio). */
export const horaEstructura = (estructura) => (esEntrada(estructura.nombreTurno) ? estructura.horaFin : estructura.horaInicio);

const aMinutos = (hora) => {
    if (!hora) return null;
    const [h, m] = hora.split(":").map((v) => parseInt(v, 10));
    return Number.isNaN(h) ? null : h * 60 + (m || 0);
};

/** Diferencia circular (en minutos) entre dos horas del día. */
const diferenciaCircular = (a, b) => {
    const d = Math.abs(a - b) % 1440;
    return Math.min(d, 1440 - d);
};

/** Duración de una jornada, considerando que la salida puede ser al día siguiente. */
const duracionJornada = (entrada, salida) => (salida - entrada + 1440) % 1440;

/**
 * Determina el esquema del cliente únicamente con los turnos y las horas de la ruta,
 * porque en base de datos no existe un dato que lo indique.
 */
const detectarEsquema = (horasPorTurno) => {
    const turnos = Object.keys(horasPorTurno);
    const hora = (turno) => horasPorTurno[turno]?.[0] ?? null;

    if (turnos.some((t) => t === "EA" || t === "SA")) {
        return { clave: "ADMIN", motivo: "La ruta tiene turnos administrativos (EA / SA)." };
    }
    if (turnos.some((t) => t === "E3" || t === "S3")) {
        return { clave: "TRES", motivo: "La ruta tiene turnos del tercer turno (E3 / S3)." };
    }

    // En 12x12 la entrada de la mañana (E1) coincide con la salida de la noche (S2)
    if (hora("E1") != null && hora("S2") != null) {
        const coinciden = diferenciaCircular(hora("E1"), hora("S2")) <= TOLERANCIA_MISMO_VIAJE_MIN;
        return coinciden
            ? { clave: "DOCE", motivo: "La entrada E1 y la salida S2 son a la misma hora (mismo viaje)." }
            : { clave: "DOS",  motivo: "La salida S2 no coincide con la entrada E1, se recoge en la noche." };
    }

    // Sin ese par, se compara la duración de la jornada: 12x12 ≈ 12 h, 2 turnos ≈ 8 h
    const jornadas = [["E1", "S1"], ["E2", "S2"]]
        .filter(([e, s]) => hora(e) != null && hora(s) != null)
        .map(([e, s]) => duracionJornada(hora(e), hora(s)));
    if (jornadas.length > 0) {
        const larga = jornadas.some((d) => d >= JORNADA_LARGA_MIN);
        return larga
            ? { clave: "DOCE", motivo: "La jornada entre entrada y salida dura cerca de 12 horas." }
            : { clave: "DOS",  motivo: "La jornada entre entrada y salida dura menos de 10 horas." };
    }

    return { clave: "DOS", motivo: "No hay suficientes horarios para calcularlo; se muestra 2 turnos por defecto.", estimado: true };
};

/**
 * Calcula el esquema de turnos de la ruta y agrupa las estructuras por momento del día
 * (las entradas y salidas que se hacen en el mismo viaje quedan juntas).
 */
export const useRelacionTurnos = (estructuras = []) => {
    const horasPorTurno = useMemo(() => {
        const mapa = {};
        estructuras.forEach((est) => {
            const minutos = aMinutos(horaEstructura(est));
            if (!est.nombreTurno || minutos == null) return;
            mapa[est.nombreTurno] = mapa[est.nombreTurno] ?? [];
            if (!mapa[est.nombreTurno].includes(minutos)) mapa[est.nombreTurno].push(minutos);
        });
        Object.values(mapa).forEach((lista) => lista.sort((a, b) => a - b));
        return mapa;
    }, [estructuras]);

    const deteccion = useMemo(() => detectarEsquema(horasPorTurno), [horasPorTurno]);
    const [esquemaManual, setEsquemaManual] = useState(null);
    const claveEsquema = esquemaManual ?? deteccion.clave;
    const esquema = ESQUEMAS[claveEsquema];

    /**
     * Las salidas flexibles (SSD) no tienen viaje fijo. Se colocan, en este orden:
     * 1. En el viaje cuyo horario coincide con el suyo.
     * 2. En el viaje de la salida que le falta a un turno (tiene entrada pero no salida).
     * 3. En el viaje más cercano por horario.
     * Devuelve Map(idEstructura → { momento, nota }).
     */
    const asignarSalidasFlexibles = (lista) => {
        const asignaciones = new Map();
        const presentes = new Set(lista.map((e) => e.nombreTurno));
        const cubiertas = new Set();

        const horasDelMomento = (m) => lista
            .filter((e) => e.nombreTurno === m.entrada || e.nombreTurno === m.salida)
            .map((e) => aMinutos(horaEstructura(e)))
            .filter((v) => v != null);
        const distancia = (m, minutos) => {
            const horas = horasDelMomento(m);
            return minutos == null || horas.length === 0
                ? Infinity
                : Math.min(...horas.map((h) => diferenciaCircular(h, minutos)));
        };
        const masCercano = (momentos, minutos) => [...momentos].sort((a, b) => distancia(a, minutos) - distancia(b, minutos))[0];

        lista
            .filter((e) => SALIDAS_FLEXIBLES.includes(e.nombreTurno))
            .forEach((est) => {
                const minutos = aMinutos(horaEstructura(est));
                const porHorario = masCercano(esquema.momentos, minutos);

                if (porHorario && distancia(porHorario, minutos) <= TOLERANCIA_MISMO_VIAJE_MIN) {
                    asignaciones.set(est.id, { momento: porHorario, nota: `Va en el viaje de ${porHorario.label.toLowerCase()} por su horario` });
                    return;
                }

                // Viajes cuya salida no está contratada pero su turno sí tiene entrada
                const sinSalida = esquema.momentos.filter((m) =>
                    m.salida && !presentes.has(m.salida) && !cubiertas.has(m.salida) && presentes.has(entradaDeSalida(m.salida))
                );
                if (sinSalida.length > 0) {
                    const momento = masCercano(sinSalida, minutos);
                    cubiertas.add(momento.salida);
                    asignaciones.set(est.id, { momento, nota: `Cubre la salida ${momento.salida} que le falta al turno ${entradaDeSalida(momento.salida)}` });
                    return;
                }

                if (porHorario && distancia(porHorario, minutos) !== Infinity) {
                    asignaciones.set(est.id, { momento: porHorario, nota: `Viaje más cercano a su horario (${porHorario.label.toLowerCase()})` });
                }
            });

        return asignaciones;
    };

    /** Devuelve [{ momento, estructuras, notas }]; los turnos fuera del esquema van al final sin momento. */
    const agrupar = (lista) => {
        const porHora = (a, b) => (aMinutos(horaEstructura(a)) ?? 0) - (aMinutos(horaEstructura(b)) ?? 0);
        const flexibles = asignarSalidasFlexibles(lista);
        const flexiblesDe = (momento) => lista.filter((e) => flexibles.get(e.id)?.momento === momento);

        const grupos = esquema.momentos
            .map((momento) => ({
                momento,
                estructuras: [
                    ...lista.filter((e) => e.nombreTurno === momento.entrada).sort(porHora),
                    ...lista.filter((e) => e.nombreTurno === momento.salida).sort(porHora),
                    ...flexiblesDe(momento).sort(porHora),
                ],
                extras: [...new Set(flexiblesDe(momento).map((e) => e.nombreTurno))],
                notas: Object.fromEntries(flexiblesDe(momento).map((e) => [e.id, flexibles.get(e.id).nota])),
            }))
            .filter((g) => g.estructuras.length > 0);

        const enEsquema = new Set(esquema.momentos.flatMap((m) => [m.entrada, m.salida]).filter(Boolean));
        const fuera = lista.filter((e) => !enEsquema.has(e.nombreTurno) && !flexibles.has(e.id));
        return fuera.length > 0 ? [...grupos, { momento: null, estructuras: fuera }] : grupos;
    };

    const cambiarEsquema = (clave) => clave && setEsquemaManual(clave === deteccion.clave ? null : clave);

    return { esquema, claveEsquema, deteccion, esquemaManual, cambiarEsquema, agrupar };
};

/**
 * Selector del tipo de cliente. Marca el esquema calculado y permite cambiarlo
 * temporalmente si el cálculo no corresponde (no se guarda).
 */
export const SelectorEsquema = ({ relacion }) => {
    const { claveEsquema, deteccion, esquemaManual, cambiarEsquema } = relacion;
    const opciones = Object.entries(ESQUEMAS).map(([clave, e]) => ({
        value: clave,
        label: clave === deteccion.clave ? `${e.label} ✓` : e.label,
    }));

    return (
        <div className="relacion-turnos__selector">
            <SelectButton value={claveEsquema} options={opciones} onChange={(e) => cambiarEsquema(e.value)} />
            <span className="relacion-turnos__motivo">
                <i className={esquemaManual ? "pi pi-user-edit" : deteccion.estimado ? "pi pi-question-circle" : "pi pi-bolt"} />
                {esquemaManual ? "Elegido manualmente (no se guarda)." : deteccion.motivo}
            </span>
        </div>
    );
};

/**
 * Envuelve las filas de un mismo viaje con una línea de color que las une
 * y una etiqueta con el momento y los turnos relacionados.
 */
export const GrupoTurnos = ({ momento, extras = [], children }) => {
    if (!momento) {
        return <div className="relacion-turnos__grupo relacion-turnos__grupo--fuera">{children}</div>;
    }

    // Las salidas flexibles (SSD) asignadas a este viaje se suman a su salida
    const salidas = [momento.salida, ...extras].filter(Boolean).join(" / ");
    const mismoViaje = Boolean(momento.entrada && salidas);
    const relacion = mismoViaje
        ? `${momento.entrada} ↔ ${salidas}`
        : `Solo ${momento.entrada ?? salidas}`;

    return (
        <div className={`relacion-turnos__grupo relacion-turnos__grupo--${momento.color}`}>
            <div className="relacion-turnos__etiqueta">
                <span className="relacion-turnos__etiqueta-momento"><i className={momento.icon} /> {momento.label}</span>
                <span className="relacion-turnos__etiqueta-turnos">{relacion}</span>
                {mismoViaje && <span className="relacion-turnos__etiqueta-nota">mismo viaje</span>}
            </div>
            <div className="relacion-turnos__filas">{children}</div>
        </div>
    );
};
